import { useCallback } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { STORAGE_KEYS } from '@/constants/storage';
import { VIEWER_SCOPED_EVIDENCE_ROOTS } from '@/hooks/pii-query-key-roots';
import { canRoleCompleteOnboarding } from '@/lib/org-onboarding-access';

import { supabase } from '../integrations/supabase/client';

import type { OrganizationMember, SwitchOrganizationOptions } from './organization-types';

const ORGANIZATION_STORAGE_KEY = STORAGE_KEYS.SELECTED_ORGANIZATION_ID;

async function removeAttachmentPreviewQueries(
  queryClient: QueryClient,
  organizationId: string | null,
  nextOrganizationId: string,
): Promise<void> {
  if (!organizationId || organizationId === 'admin' || organizationId === nextOrganizationId) {
    return;
  }

  for (const queryKey of [
    ['attachment-preview-blob', organizationId],
    ['attachment-office-preview', organizationId],
    // Keys carry actor + organisation, so a stale entry could not be served in
    // the new office anyway; cancelling drops an in-flight response for the old
    // one, and removal keeps its titles, paths and excerpts out of memory.
    ...VIEWER_SCOPED_EVIDENCE_ROOTS.map((root) => [root]),
  ]) {
    await queryClient.cancelQueries({ queryKey });
    queryClient.removeQueries({ queryKey });
  }
}

interface UseOrganizationSwitchingParams {
  user: { id: string } | null;
  currentOrganizationId: string | null;
  setCurrentOrganizationId: (id: string | null) => void;
  userOrganizations: OrganizationMember[];
  isPlatformAdmin: boolean;
  setIsSwitching: (value: boolean) => void;
  setIsDeleting: (value: boolean) => void;
}

export function useOrganizationSwitching({
  user,
  currentOrganizationId,
  setCurrentOrganizationId,
  userOrganizations,
  isPlatformAdmin,
  setIsSwitching,
  setIsDeleting,
}: UseOrganizationSwitchingParams) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const switchOrganization = useCallback(
    (organizationId: string, options?: SwitchOrganizationOptions) => {
      // Callers that just refetched memberships pass the fresh list; the
      // hook's own `userOrganizations` is a closure capture and can lag it.
      // An EMPTY override falls back to the hook list — a transient refetch
      // failure must not erase memberships the hook still knows about.
      const organizations = options?.organizations?.length
        ? options.organizations
        : userOrganizations;

      setIsSwitching(true);

      if (currentOrganizationId && currentOrganizationId !== 'admin') {
        // Fire-and-forget: cache invalidation is best-effort and must not block
        // the switch. Swallow rejection so it stays off the unhandled path.
        queryClient
          .invalidateQueries({
            queryKey: ['organization-members-cache', currentOrganizationId, user?.id],
          })
          .catch(() => {
            /* best-effort */
          });
      }

      const resetFlag = () => {
        setTimeout(() => {
          setIsSwitching(false);
        }, 100);
      };

      const updateAndNavigate = async (orgId: string, path: string) => {
        await removeAttachmentPreviewQueries(queryClient, currentOrganizationId, orgId);
        localStorage.setItem(ORGANIZATION_STORAGE_KEY, orgId);
        setCurrentOrganizationId(orgId);

        if (orgId !== 'admin') {
          try {
            const { data, error } = await supabase
              .from('organizations')
              .select('settings')
              .eq('id', orgId)
              .single();

            if (!error && data) {
              const settings = data.settings as { onboarding_completed?: boolean } | null;
              // Onboarding is an admin task — only funnel roles that can
              // complete it (write org settings). A non-admin whose completion
              // write RLS-no-ops would be looped back here on every switch.
              const membershipRole = organizations.find((om) => om.organization_id === orgId)?.role;
              if (!settings?.onboarding_completed && canRoleCompleteOnboarding(membershipRole)) {
                sessionStorage.setItem('onboarding_organization_id', orgId);
                Promise.resolve(navigate('/onboarding')).catch(() => null);
                resetFlag();
                return;
              }
            }
          } catch (error) {
            console.error('Error checking onboarding status:', error);
          }
        }

        if (!options?.preserveRoute) {
          Promise.resolve(navigate(path)).catch(() => null);
        }
        resetFlag();
      };

      if (organizationId === 'admin') {
        if (isPlatformAdmin) {
          void updateAndNavigate('admin', '/admin');
        }
        return;
      }

      // `userOrganizations` is already filtered to active rows in
      // organization-context, so checking `status === 'active'` here is
      // belt-and-braces. Pending invitations belong on a separate read path
      // (RPC guards reject them with 403); never let one drive currentOrg.
      const hasMembership = organizations.some(
        (om) => om.organization_id === organizationId && om.status === 'active',
      );

      if (!hasMembership) {
        console.warn(
          `User attempted to access organization ${organizationId} but has no active membership. Redirecting to available organization.`,
        );

        if (organizations.length > 0) {
          void updateAndNavigate(organizations[0].organization_id, '/');
        } else if (isPlatformAdmin) {
          void updateAndNavigate('admin', '/admin');
        } else {
          Promise.resolve(navigate('/')).catch(() => null);
          setIsSwitching(false);
        }
        return;
      }

      void updateAndNavigate(organizationId, '/');
    },
    [
      currentOrganizationId,
      queryClient,
      user?.id,
      setCurrentOrganizationId,
      navigate,
      isPlatformAdmin,
      userOrganizations,
      setIsSwitching,
    ],
  );

  const handleOrganizationDeletion = useCallback(
    (deletedOrgId: string) => {
      setIsDeleting(true);

      if (currentOrganizationId === deletedOrgId) {
        if (isPlatformAdmin) {
          setCurrentOrganizationId('admin');
          localStorage.setItem(ORGANIZATION_STORAGE_KEY, 'admin');
        } else {
          const remainingOrgs = userOrganizations.filter(
            (om) => om.organization_id !== deletedOrgId,
          );
          if (remainingOrgs.length > 0) {
            const newOrgId = remainingOrgs[0].organization_id;
            setCurrentOrganizationId(newOrgId);
            localStorage.setItem(ORGANIZATION_STORAGE_KEY, newOrgId);
          } else {
            setCurrentOrganizationId(null);
            localStorage.removeItem(ORGANIZATION_STORAGE_KEY);
          }
        }
      }

      setTimeout(() => {
        setIsDeleting(false);
      }, 3000);
    },
    [
      currentOrganizationId,
      isPlatformAdmin,
      userOrganizations,
      setCurrentOrganizationId,
      setIsDeleting,
    ],
  );

  return { switchOrganization, handleOrganizationDeletion };
}
